import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import AIFormGenerator from '@/components/AIFormGenerator';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  type GeneratedForm,
  generateForm
} from '@/services/aiFormService';
import {
  Loader2,
  RefreshCw,
  Edit,
  Save,
  CheckCircle,
  AlertCircle,
  Type,
  Mail,
  Phone,
  List,
  AlignLeft,
  CircleDot,
  CheckSquare,
  Hash,
  Calendar,
  Upload,
  Sparkles,
} from 'lucide-react';

const fieldIcons: Record<string, React.ReactNode> = {
  text: <Type className="h-4 w-4" />,
  email: <Mail className="h-4 w-4" />,
  phone: <Phone className="h-4 w-4" />,
  select: <List className="h-4 w-4" />,
  radio: <CircleDot className="h-4 w-4" />,
  checkbox: <CheckSquare className="h-4 w-4" />,
  textarea: <AlignLeft className="h-4 w-4" />,
  number: <Hash className="h-4 w-4" />,
  date: <Calendar className="h-4 w-4" />,
  file: <Upload className="h-4 w-4" />,
};

const AIBuilder = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { toast } = useToast();

  const [generatedForm, setGeneratedForm] = useState<GeneratedForm | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  
  const [refinePrompt, setRefinePrompt] = useState("");
  const [isRefining, setIsRefining] = useState(false);
  const [refineError, setRefineError] = useState<string | null>(null);

  const handleRefine = async () => {
    if (!refinePrompt.trim() || !generatedForm) return;
    
    setIsRefining(true);
    setRefineError(null);
    try {
      const data = await generateForm(refinePrompt, generatedForm);
      try {
        const cleanedData = (typeof data === 'string' ? data : JSON.stringify(data)).trim().replace(/^```json/, '').replace(/```$/, '');
        const updatedForm = JSON.parse(cleanedData);
        if (updatedForm && typeof updatedForm === 'object' && Array.isArray(updatedForm.fields)) {
          setGeneratedForm(updatedForm);
          setRefinePrompt("");
        } else {
          setGeneratedForm(data);
          setRefinePrompt("");
        }
      } catch (e) {
        if (data && typeof data === 'object' && Array.isArray(data.fields)) {
          setGeneratedForm(data);
          setRefinePrompt("");
        } else {
          throw new Error('AI returned invalid form structure');
        }
      }
    } catch (err: any) {
      if (err.message?.includes('AI_QUOTA_EXCEEDED')) {
        setRefineError("QUOTA_REACHED");
      } else {
        setRefineError(err.message || 'Failed to refine form. Try again.');
      }
    } finally {
      setIsRefining(false);
    }
  };

  const handleRefineKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isRefining && refinePrompt.trim()) {
        handleRefine();
      }
    }
  };

  const handleSave = async (publish: boolean) => {
    if (!generatedForm || !user) return;
    setIsSaving(true);
    try {
      const { data: newForm, error } = await supabase
        .from('forms')
        .insert({
          user_id: user.id,
          title: (generatedForm.title || 'Untitled AI Form').trim() || 'Untitled AI Form',
          description: (generatedForm.description || '').trim(),
          fields: (Array.isArray(generatedForm.fields) ? generatedForm.fields : []).map((f, i) => ({
            id: String(f.id || `field_${Date.now()}_${i}`),
            type: (f.type || 'text'),
            label: String(f.label || 'New Field'),
            placeholder: String(f.placeholder || ''),
            required: !!f.required,
            options: Array.isArray(f.options) ? f.options.map(String) : []
          })),
          is_published: publish,
          whatsapp_number: generatedForm.whatsapp_number || '',
          settings: { whatsappMessage: 'Hi! I would like to submit this form: {form_data}' },
        })
        .select('id')
        .single();

      if (error) throw error;
      toast({ title: publish ? 'Form published!' : 'Form saved to builder!' });
      navigate(`/form-builder/${newForm.id}`);
    } catch (err: any) {
      toast({ title: 'Failed to save', description: err.message, variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setGeneratedForm(null);
    setRefinePrompt("");
  };

  const [intent] = useState(() => {
    const fromUrl = searchParams.get('intent');
    if (fromUrl) return fromUrl;
    const fromStorage = sessionStorage.getItem('pending_intent');
    if (fromStorage) {
      sessionStorage.removeItem('pending_intent');
      return fromStorage;
    }
    return '';
  });

  return (
    <DashboardLayout>
      <Helmet>
        <title>Create with AI | Bravax Forms</title>
      </Helmet>
      {!generatedForm ? (
        <div className="flex-1 flex flex-col items-center justify-center py-8 px-4 h-full min-h-[500px]">
          <AIFormGenerator onFormGenerated={setGeneratedForm} initialPrompt={intent} />
        </div>
      ) : (
        /* Side-by-side: Refine Controls + Form Preview */
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-[360px_1fr] h-full min-h-0 overflow-hidden gap-6 animate-in fade-in duration-300">
          
          {/* Left Column: AI Refinement Controls & Actions */}
          <div className="flex flex-col bg-card border border-border/40 rounded-[2rem] p-6 justify-between overflow-y-auto">
            <div className="space-y-6">
              
              {/* Badge & Title */}
              <div className="space-y-3">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold border border-primary/20">
                  <CheckCircle className="h-3.5 w-3.5" />
                  Generated · {generatedForm.fields.length} fields
                </div>
                <h2 className="text-xl font-display font-bold text-foreground leading-tight">{generatedForm.title}</h2>
                <p className="text-xs text-muted-foreground leading-relaxed">{generatedForm.description}</p>
              </div>

              {/* Architect Interaction Input */}
              <div className="bg-muted/30 rounded-2xl p-4 space-y-3 border border-border/30">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold text-primary uppercase tracking-wider">Refine with AI</span>
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                </div>
                <Textarea
                  placeholder="Ask AI to refine this form (e.g. Add a rating field, change placeholders)..."
                  className="min-h-[90px] resize-none text-sm bg-background border-border/50 rounded-xl focus-visible:ring-1 focus-visible:ring-primary p-3"
                  value={refinePrompt}
                  onChange={(e) => setRefinePrompt(e.target.value)}
                  onKeyDown={handleRefineKeyDown}
                  disabled={isRefining}
                />
                {refineError && (
                  <div className="p-3 rounded-lg text-xs bg-destructive/10 text-destructive border border-destructive/20">
                    {refineError}
                  </div>
                )}
                <Button 
                  onClick={handleRefine} 
                  disabled={isRefining || !refinePrompt.trim()} 
                  className="w-full h-10 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-semibold gap-2 shadow-sm"
                >
                  {isRefining ? <><Loader2 className="h-4 w-4 animate-spin"/> Applying Refinements...</> : <><RefreshCw className="h-4 w-4" /> Apply Refinements</>}
                </Button>
              </div>

            </div>

            {/* Bottom Actions Row: Builder, Reset, Save/Publish */}
            <div className="pt-6 border-t border-border/40 space-y-2 mt-6">
              <div className="grid grid-cols-2 gap-2">
                <Button 
                  onClick={() => handleSave(false)} 
                  disabled={isSaving} 
                  variant="outline" 
                  className="w-full gap-1.5 h-10 rounded-xl text-xs font-semibold border-border/60"
                  title="Open in full form editor"
                >
                  <Edit className="h-3.5 w-3.5" /> Builder
                </Button>
                <Button 
                  onClick={handleReset} 
                  variant="ghost" 
                  className="w-full gap-1.5 h-10 rounded-xl text-xs font-medium text-muted-foreground hover:text-foreground"
                >
                  <RefreshCw className="h-3.5 w-3.5" /> Reset
                </Button>
              </div>
              
              <Button 
                onClick={() => handleSave(true)} 
                disabled={isSaving} 
                className="w-full h-12 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-sm gap-2 shadow-lg shadow-primary/20"
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save & Publish Final Form
              </Button>
            </div>

          </div>

          {/* Right Column: Resulting Live Form Preview */}
          <div className="bg-card border border-border/40 rounded-[2rem] p-6 lg:p-10 overflow-y-auto relative">
            <div className="max-w-2xl mx-auto space-y-8">
              
              {/* Form Preview Header */}
              <div className="border-b border-border/40 pb-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-[10px] font-bold uppercase tracking-wider mb-3">
                  Live Form Preview
                </div>
                <h3 className="text-2xl md:text-3xl font-display font-bold text-foreground">{generatedForm.title}</h3>
                {generatedForm.description && (
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{generatedForm.description}</p>
                )}
              </div>

              {/* Form Fields */}
              <div className="space-y-6">
                {generatedForm.fields.map((field) => (
                  <div key={field.id} className="space-y-2 text-left">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                        <span className="text-primary">{fieldIcons[field.type] || <Type className="h-3.5 w-3.5" />}</span>
                        {field.label}
                        {field.required && <span className="text-primary">*</span>}
                      </label>
                      <Badge variant="outline" className="text-[9px] uppercase tracking-wider opacity-60">
                        {field.type}
                      </Badge>
                    </div>

                    <div>
                      {field.type === 'textarea' ? (
                        <div className="min-h-[100px] rounded-xl border border-border/50 bg-muted/20 px-4 py-3 text-sm text-muted-foreground italic">
                          {field.placeholder || 'Write your response here...'}
                        </div>
                      ) : field.type === 'select' || field.type === 'radio' || field.type === 'checkbox' ? (
                        <div className="space-y-2">
                          {field.options?.map((opt, oi) => (
                            <div key={oi} className="flex items-center gap-3 text-sm text-muted-foreground bg-muted/20 p-3 rounded-xl border border-border/30">
                              <div className={cn(
                                "w-4 h-4 border-2 border-primary/40 shrink-0",
                                field.type === 'radio' ? "rounded-full" : "rounded-md"
                              )} />
                              {opt}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="h-12 rounded-xl border border-border/50 bg-muted/20 px-4 flex items-center text-sm text-muted-foreground italic">
                          {field.placeholder || `Enter ${field.type}...`}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

            </div>
          </div>

        </div>
      )}
    </DashboardLayout>
  );
};

export default AIBuilder;
